// src/utils/xlsxLecture.ts — SCE Issues
// Lire un classeur Excel par son XML : onglets, textes partagés, cellules,
// tableaux Excel. Partagé par la déclaration NC-S (le formulaire et sa liste
// de personnes) et la recherche du N° NC-S dans le registre qualité (06/10).
//
// Pur : aucun React, aucun réseau. JSZip est chargé par l'appelant.

import type JSZip from 'jszip'

export interface FeuilleDuClasseur {
  nom: string
  /** Chemin de la feuille dans l'archive (`xl/worksheets/sheet1.xml`). */
  chemin: string
}

export async function lire(zip: JSZip, chemin: string): Promise<string> {
  const f = zip.file(chemin)
  if (!f) throw new Error(`Classeur NC-S incomplet : ${chemin} introuvable`)
  return f.async('string')
}

/** Attributs d'une balise : `<sheet name="X" r:id="rId1"/>` → { name: 'X', 'r:id': 'rId1' }. */
export function attributs(balise: string): Record<string, string> {
  const res: Record<string, string> = {}
  for (const m of balise.matchAll(/([\w:]+)="([^"]*)"/g)) res[m[1] ?? ''] = desechapper(m[2] ?? '')
  return res
}

export function desechapper(t: string): string {
  return t
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&')
}

/** `worksheets/sheet1.xml` vu depuis `xl/` → `xl/worksheets/sheet1.xml` ; gère `../` et les chemins absolus. */
export function resoudre(dossier: string, cible: string): string {
  if (cible.startsWith('/')) return cible.slice(1)
  const parts = dossier.split('/').filter(Boolean)
  for (const p of cible.split('/')) {
    if (p === '..') parts.pop()
    else if (p !== '.') parts.push(p)
  }
  return parts.join('/')
}

export async function feuillesDuClasseur(zip: JSZip): Promise<FeuilleDuClasseur[]> {
  const classeur = await lire(zip, 'xl/workbook.xml')
  const rels = await lire(zip, 'xl/_rels/workbook.xml.rels')
  const cibles = new Map<string, string>()
  for (const m of rels.matchAll(/<Relationship\b[^>]*>/g)) {
    const a = attributs(m[0])
    if (a.Id && a.Target) cibles.set(a.Id, resoudre('xl', a.Target))
  }
  return [...classeur.matchAll(/<sheet\b[^>]*\/>/g)].map((m) => {
    const a = attributs(m[0])
    return {
      nom: a.name ?? '',
      chemin: cibles.get(a['r:id'] ?? '') ?? '',
    }
  })
}

/** Textes partagés du classeur, par index. */
export async function textesPartages(zip: JSZip): Promise<string[]> {
  const f = zip.file('xl/sharedStrings.xml')
  if (!f) return []
  const xml = await f.async('string')
  return [...xml.matchAll(/<si>([\s\S]*?)<\/si>/g)].map((m) =>
    desechapper([...(m[1] ?? '').matchAll(/<t[^>]*>([^<]*)<\/t>/g)].map((t) => t[1] ?? '').join('')),
  )
}

// ─── Cellules et tableaux Excel ───

/** `AB` → 27 (A = 1). */
export function indexColonne(lettres: string): number {
  let n = 0
  for (const c of lettres) n = n * 26 + (c.charCodeAt(0) - 64)
  return n
}

/** Valeur d'une cellule déjà lue : texte, ou nombre (dates comprises, en numéro de série Excel). */
export type ValeurLue = string | number

/**
 * Cellules d'une feuille, ligne par ligne : `{ 12: { 1: 'Code type', … } }`.
 * Seules les lignes de `premiere` à `derniere` sont décodées (une feuille de
 * 9 Mo se parcourt alors en quelques dizaines de millisecondes).
 */
export function lireCellules(
  xml: string,
  partages: readonly string[],
  premiere: number,
  derniere: number,
): Map<number, Map<number, ValeurLue>> {
  const lignes = new Map<number, Map<number, ValeurLue>>()
  for (const r of xml.matchAll(/<row r="(\d+)"[^>]*>([\s\S]*?)<\/row>/g)) {
    const n = Number(r[1])
    if (n < premiere || n > derniere) continue
    const cellules = new Map<number, ValeurLue>()
    for (const c of (r[2] ?? '').matchAll(/<c r="([A-Z]+)\d+"([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const type = /\st="(\w+)"/.exec(c[2] ?? '')?.[1]
      const corps = c[3] ?? ''
      const v = /<v[^>]*>([^<]*)<\/v>/.exec(corps)?.[1]
      let valeur: ValeurLue | undefined
      if (type === 's') valeur = partages[Number(v)]
      else if (type === 'inlineStr') valeur = [...corps.matchAll(/<t[^>]*>([^<]*)<\/t>/g)].map((t) => desechapper(t[1] ?? '')).join('')
      else if (type === 'str' || type === 'e') valeur = v !== undefined ? desechapper(v) : undefined
      else if (v !== undefined && v !== '') valeur = Number(v)
      if (valeur !== undefined && valeur !== '') cellules.set(indexColonne(c[1] ?? ''), valeur)
    }
    lignes.set(n, cellules)
  }
  return lignes
}

/** Nom de colonne d'un tableau : `Recording date_x000a_(in the tool)` → `Recording date (in the tool)`. */
export function nomColonne(brut: string): string {
  return desechapper(brut).replace(/_x000[aAdD]_/g, ' ').replace(/\s+/g, ' ').trim()
}

/**
 * Les lignes d'un tableau Excel (Insertion › Tableau) d'un onglet, en objets
 * `{ 'NC #': 'NC-S_2026-97', … }` — seulement les colonnes demandées.
 * @param facultatives colonnes lues si elles existent (absentes : `undefined`).
 * @throws Error si l'onglet, le tableau ou une colonne obligatoire est introuvable.
 */
export async function lireTableau(
  zip: JSZip,
  onglet: string,
  colonnes: readonly string[],
  facultatives: readonly string[] = [],
): Promise<Record<string, ValeurLue | undefined>[]> {
  const feuille = (await feuillesDuClasseur(zip)).find((f) => f.nom.trim() === onglet)
  if (!feuille?.chemin) throw new Error(`Onglet « ${onglet} » introuvable`)
  const i = feuille.chemin.lastIndexOf('/')
  const rels = zip.file(`${feuille.chemin.slice(0, i)}/_rels/${feuille.chemin.slice(i + 1)}.rels`)
  const cibles = rels
    ? [...(await rels.async('string')).matchAll(/<Relationship\b[^>]*>/g)]
        .map((m) => attributs(m[0]))
        .filter((a) => /\/table$/.test(a.Type ?? '') && a.Target)
        .map((a) => resoudre(feuille.chemin.slice(0, i), a.Target ?? ''))
    : []
  const tableau = cibles[0] ? await lire(zip, cibles[0]) : ''
  const ref = /<table\b[^>]*\sref="([A-Z]+)(\d+):([A-Z]+)(\d+)"/.exec(tableau)
  if (!ref) throw new Error(`Aucun tableau Excel dans l'onglet « ${onglet} »`)
  const colDebut = indexColonne(ref[1] ?? 'A')
  const ligneEntete = Number(ref[2])
  const ligneFin = Number(ref[4])
  const noms = [...tableau.matchAll(/<tableColumn\b[^>]*\sname="([^"]*)"/g)].map((m) => nomColonne(m[1] ?? ''))
  const positions = new Map(colonnes.map((c) => [c, noms.indexOf(c)]))
  const absentes = colonnes.filter((c) => (positions.get(c) ?? -1) < 0)
  if (absentes.length > 0) throw new Error(`Colonnes introuvables dans « ${onglet} » : ${absentes.join(', ')}`)
  for (const c of facultatives) {
    const p = noms.indexOf(c)
    if (p >= 0) positions.set(c, p)
  }

  const cellules = lireCellules(await lire(zip, feuille.chemin), await textesPartages(zip), ligneEntete + 1, ligneFin)
  const resultat: Record<string, ValeurLue | undefined>[] = []
  for (const [, ligne] of [...cellules].sort((a, b) => a[0] - b[0])) {
    if (ligne.size === 0) continue
    const objet: Record<string, ValeurLue | undefined> = {}
    for (const [c, p] of positions) objet[c] = ligne.get(colDebut + p)
    resultat.push(objet)
  }
  return resultat
}

/** Numéro de série Excel → jour `AAAA-MM-JJ` ; null si ce n'est pas une date. */
export function jourExcel(v: ValeurLue | undefined): string | null {
  if (typeof v !== 'number' || !(v > 1)) return null
  const d = new Date(Date.UTC(1899, 11, 30) + Math.round(v) * 86_400_000)
  return d.toISOString().slice(0, 10)
}
